import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { SessionProvider } from './session.js';
import type { SessionUser } from './api.js';
import { ToastProvider } from '../design-system/index.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE PROVIDER STACK, AS A COMPONENT, BECAUSE A JSX TREE IN AN ENTRY POINT IS
 * A THING NO TEST EVER RENDERS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This exists because of a bug a real user hit on a real phone: **`ToastProvider` was missing
 * from the app**, so every page that calls `useToast()` — `/extensions` and `/numbers`, i.e.
 * both of WP6's pages — threw on render and showed an error screen.
 *
 * Three things had to line up for that to reach production, and each is worth naming:
 *
 * 1. **The edit silently failed.** It was a string replacement against `main.tsx`, and the
 *    render tree had been re-indented by an earlier change, so the pattern no longer matched.
 *    Nothing errored; the import line matched and the JSX did not.
 * 2. **`tsc` could not see it.** `noUnusedLocals` was off, so an imported-and-unused
 *    `ToastProvider` compiled cleanly. That is now on — an unused import is the exact
 *    fingerprint of a half-applied edit, and it is worth a build error.
 * 3. **Every test supplied its own provider.** `ExtensionsPage.test.tsx` and
 *    `NumbersPage.test.tsx` both wrap the page in `<ToastProvider>` to render it, which is
 *    correct for a unit test and meant the suite proved "this page works GIVEN a provider"
 *    while nothing proved the app supplied one. A green suite against a broken app.
 *
 * So the stack lives here, `main.tsx` renders this and nothing else, and
 * `AppProviders.test.tsx` asserts that a component using each context actually works inside
 * it. The composition is now a thing that can fail a test rather than a thing somebody has to
 * remember.
 */
export function AppProviders({
  queryClient,
  initialUser,
  children,
}: {
  queryClient: QueryClient;
  initialUser: SessionUser | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <SessionProvider initialUser={initialUser}>
      <QueryClientProvider client={queryClient}>
        {/*
          ABOVE the router, so a toast raised by a mutation survives the navigation that
          mutation triggers — a "saved" confirmation that unmounts with the page which raised
          it is a confirmation nobody sees.
        */}
        <ToastProvider>{children}</ToastProvider>
      </QueryClientProvider>
    </SessionProvider>
  );
}
